import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {Location} from '@angular/common';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';
import {ConfiguratorPageHtml5Service, DatastoreDto, PageHtml5DTO} from '@tj/tj-dmp-lib-api';
import {MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {Subject, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {analyzeTemplate, TemplateAnalysis} from './template-variables';
import {PreviewTemplate} from './live-preview/live-preview.component';
import {TestData} from './test-data/test-data.component';

type SourceTab = 'html' | 'css' | 'typescript';
type Tab = SourceTab | 'data';

const LIST_URL = '/h/landing-page/page';
/** Données de test par tenant et par template : `dmp.landing-page.test-data.<tenant>.<code>`. */
const TEST_DATA_STORAGE_PREFIX = 'dmp.landing-page.test-data';
/** Délai après la dernière frappe avant d'analyser le template et de rafraîchir l'aperçu. */
const REFRESH_DELAY_MS = 400;

/** Code d'un nouveau template, calculé d'après son nom. Il ne change plus après la création. */
export function templateCode(name: string): string {
    return 'HTML5_' + (name ?? '').trim().toUpperCase().replace(/\s+/g, '-');
}

/**
 * Création et modification d'un template HTML de landing page (cf. docs/ui-design-guidelines.md §12, §17) :
 * informations générales, puis un éditeur à deux colonnes — le code et les données de test à gauche,
 * l'aperçu en direct à droite.
 */
@Component({
    templateUrl: './template-edit.component.html',
    styleUrls: ['./template-edit.component.scss'],
    standalone: false
})
export class TemplateEditComponent implements OnInit, OnDestroy {
    breadcrumb: { label: string }[] = [];
    form: UntypedFormGroup;
    actual: PageHtml5DTO;
    loaded = false;
    saving = false;
    submitted = false;

    source: Record<SourceTab, string> = {html: '', css: '', typescript: ''};
    activeTab: Tab = 'html';
    readonly tabs: { label: string; value: Tab; icon: string }[] = [
        {label: 'HTML', value: 'html', icon: 'fab fa-html5'},
        {label: 'CSS', value: 'css', icon: 'fab fa-css3-alt'},
        {label: 'TypeScript', value: 'typescript', icon: 'fab fa-js'},
        {label: this.translate.instant('landingPage.template.edit.testDataTab'), value: 'data', icon: 'fas fa-database'}
    ];
    editorOptions = {
        theme: 'vs',
        language: 'html',
        lineNumbers: 'on',
        minimap: {enabled: false},
        scrollBeyondLastLine: false,
        // L'éditeur suit la largeur de sa colonne quand l'aperçu est agrandi ou réduit.
        automaticLayout: true
    };

    analysis: TemplateAnalysis = {variables: [], steps: []};
    previewTemplate: PreviewTemplate;
    testData: TestData = {};
    previewExpanded = false;

    private _group: DatastoreDto;
    private readonly sourceChange = new Subject<void>();
    private subscription: Subscription;

    constructor(private fb: UntypedFormBuilder, private templateHtmlService: ConfiguratorPageHtml5Service,
                private route: ActivatedRoute, private router: Router, private location: Location,
                private messageService: MessageService, private readonly translate: TranslateService) {
    }

    get group(): DatastoreDto {
        return this._group;
    }

    set group(group: DatastoreDto) {
        this._group = group;
        if (group) {
            this.load();
        }
    }

    get f(): { [key: string]: AbstractControl } {
        return this.form.controls;
    }

    /** Figé à la création : les configurations désignent le template par ce code. */
    get code(): string {
        return this.actual?.code ?? templateCode(this.form.value.name);
    }

    get title(): string {
        return this.actual
            ? this.translate.instant('landingPage.template.edit.editTitle', {name: this.actual.name})
            : this.translate.instant('landingPage.template.edit.newTitle');
    }

    get editorValue(): string {
        return this.activeTab === 'data' ? '' : this.source[this.activeTab];
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            name: ['', Validators.required],
            description: [''],
        });
        this.updateBreadcrumb();
        this.subscription = this.sourceChange.pipe(debounceTime(REFRESH_DELAY_MS)).subscribe(() => this.refresh());
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    /** Ctrl+S (Cmd+S) enregistre sans quitter, comme dans un éditeur de code. */
    @HostListener('document:keydown', ['$event'])
    onKeydown(event: KeyboardEvent): void {
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
            event.preventDefault();
            this.save(false);
        }
    }

    showError(control: string): boolean {
        const c = this.f[control];
        return c.invalid && (c.dirty || c.touched || this.submitted);
    }

    onTabChange(): void {
        if (this.activeTab !== 'data') {
            this.editorOptions = {...this.editorOptions, language: this.activeTab};
        }
    }

    onSourceChange(value: string): void {
        if (this.activeTab === 'data' || value === this.source[this.activeTab]) {
            return;
        }
        this.source = {...this.source, [this.activeTab]: value};
        this.sourceChange.next();
    }

    onTestDataChange(data: TestData): void {
        this.testData = data;
        this.writeTestData();
    }

    save(close: boolean): void {
        this.submitted = true;
        this.form.markAllAsTouched();
        if (this.form.invalid || this.saving || !this.group) {
            return;
        }
        this.saving = true;
        const value = this.form.value;
        const template: PageHtml5DTO = {
            code: this.code,
            name: value.name.trim(),
            description: value.description,
            html: this.source.html,
            css: this.source.css,
            typescript: this.source.typescript
        };
        const created = !this.actual;
        const request = created
            ? this.templateHtmlService.createPageHtml5UsingPOST(this.group.code, template)
            : this.templateHtmlService.updatePageHtml5UsingPUT(this.group.code, {...template, id: this.actual.id});

        request.subscribe({
            next: (saved) => {
                this.saving = false;
                if (close) {
                    const navigationExtras: NavigationExtras = {state: {message: created ? 'success_created' : 'success_updated'}};
                    this.router.navigate([LIST_URL], navigationExtras);
                    return;
                }
                const previousKey = this.storageKey();
                this.actual = {...template, ...saved, id: saved?.id ?? this.actual?.id};
                if (created) {
                    // Les données de test saisies avant la création suivent le template sous son code.
                    this.moveTestData(previousKey);
                    this.location.replaceState(`${LIST_URL}/${this.actual.id}/edit`);
                }
                this.form.markAsPristine();
                this.updateBreadcrumb();
                this.messageService.add({severity: 'success', summary: this.translate.instant('landingPage.template.edit.savedSummary'), detail: this.actual.name});
            },
            error: (e) => {
                this.saving = false;
                this.messageService.add({severity: 'error', summary: this.translate.instant('landingPage.template.edit.notSavedSummary'), detail: e?.message ?? String(e)});
            }
        });
    }

    private load(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (!id) {
            this.ready();
            return;
        }
        this.templateHtmlService.getPageHtml5UsingGET(this.group.code, id).subscribe({
            next: (template) => {
                this.actual = template;
                this.form.reset({name: template.name, description: template.description});
                this.source = {html: template.html ?? '', css: template.css ?? '', typescript: template.typescript ?? ''};
                this.updateBreadcrumb();
                this.ready();
            },
            error: (e) => {
                this.messageService.add({severity: 'error', summary: this.translate.instant('landingPage.template.edit.notFoundSummary'), detail: e?.message ?? String(e)});
            }
        });
    }

    private ready(): void {
        this.testData = this.readTestData();
        this.refresh();
        this.loaded = true;
    }

    /** Analyse le template pour les données de test, et envoie la version en cours à l'aperçu. */
    private refresh(): void {
        this.analysis = analyzeTemplate(this.source.html, this.source.typescript);
        this.previewTemplate = {code: this.code, ...this.source};
    }

    private updateBreadcrumb(): void {
        this.breadcrumb = [
            {label: this.translate.instant('menu.landingpage.label')},
            {label: this.translate.instant('menu.landingpage.html-template')},
            {label: this.actual?.name ?? this.translate.instant('landingPage.template.edit.newTitle')}
        ];
    }

    /* Données de test : propres à ce navigateur (§17). Toute lecture ou écriture peut échouer en navigation
       privée ou stockage plein ; l'aperçu fonctionne alors sans les conserver. */

    private storageKey(): string {
        return `${TEST_DATA_STORAGE_PREFIX}.${this.group?.code}.${this.actual?.code ?? 'new'}`;
    }

    private readTestData(): TestData {
        try {
            const data = JSON.parse(localStorage.getItem(this.storageKey()) ?? '{}');
            return data && typeof data === 'object' && !Array.isArray(data) ? data : {};
        } catch {
            return {};
        }
    }

    private writeTestData(): void {
        try {
            if (Object.keys(this.testData).length) {
                localStorage.setItem(this.storageKey(), JSON.stringify(this.testData));
            } else {
                localStorage.removeItem(this.storageKey());
            }
        } catch {
            // Stockage indisponible : les données valent pour cette page seulement.
        }
    }

    private moveTestData(previousKey: string): void {
        try {
            localStorage.removeItem(previousKey);
        } catch {
            // idem
        }
        this.writeTestData();
    }
}
